import { useState, useCallback, useEffect, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { format } from "date-fns";
import { DateRange } from "react-day-picker"
import createAxiosInstance from "@/pages/ConversionReportPage/components/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { setDate } from "@/store/slices/DateSlice";
import ReportTable from "./ReportTable";
import Loader from "@/components/dashboard_component/loader";

interface DaywiseMetric {
  "Day": string
  "Add To Cart": string
  "Checkouts": string
  "Sessions": string
  "Purchases": string
  "Purchase Rate": string
  "Add To Cart Rate": string
  "Checkout Rate": string
  "ATC To Checkout Rate": string
  "Checkout To Purchase Rate": string
}

interface DaywiseMetricProps {
  dateRange: DateRange | undefined;
  visibleColumns: string[];
  columnOrder: string[];
  refreshTrigger: number;
  hasGA4Account?: boolean;
}

const DaywiseMetricsPage: React.FC<DaywiseMetricProps> = ({ 
  dateRange: propDateRange, 
   visibleColumns,
  columnOrder,
  refreshTrigger,
  hasGA4Account = true,
}) => {
  const dateFrom = useSelector((state: RootState) => state.date.from);
  const dateTo = useSelector((state: RootState) => state.date.to);
  const date = useMemo(() => ({
    from: dateFrom,
    to: dateTo
  }), [dateFrom, dateTo]);
  const [data, setData] = useState<DaywiseMetric[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { brandId } = useParams();

  
  const startDate = date?.from ? format(date.from, "yyyy-MM-dd") : "";
  const endDate = date?.to ? format(date.to, "yyyy-MM-dd") : "";

  
  const dispatch = useDispatch();
  const axiosInstance = createAxiosInstance();

  // Transform data to match ReportTable's expected format
  const transformedData = useMemo(() => {
    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dataMap = new Map(data.map(item => [item.Day, item]));

    return daysOfWeek.map((day, index) => {
      const item = dataMap.get(day);

      if (!item) {
        return {
          id: `row-${index}`,
          day: day,
          sessions: undefined as any,
          addToCart: undefined as any,
          addToCartRate: '-',
          checkouts: undefined as any,
          checkoutRate: '-',
          purchases: undefined as any,
          purchaseRate: '-',
          atcToCheckoutRate: '-',
          checkoutToPurchaseRate: '-'
        };
      }

      return {
        id: `row-${index}`,
        day: item.Day,
        sessions: item.Sessions !== undefined ? parseInt(item.Sessions as string) : undefined as any,
        addToCart: item['Add To Cart'] !== undefined ? parseInt(item['Add To Cart'] as string) : undefined as any,
        addToCartRate: item['Add To Cart Rate'] || '-',
        checkouts: item['Checkouts'] !== undefined ? parseInt(item['Checkouts'] as string) : undefined as any,
        checkoutRate: item['Checkout Rate'] || '-',
        purchases: item['Purchases'] !== undefined ? parseInt(item['Purchases'] as string) : undefined as any,
        purchaseRate: item['Purchase Rate'] || '-',
        atcToCheckoutRate: item['ATC To Checkout Rate'] || '-',
        checkoutToPurchaseRate: item['Checkout To Purchase Rate'] || '-'
      };
    });
  }, [data]);

  // Consolidate date handling into a single useEffect
  useEffect(() => {
    if (propDateRange) {
      dispatch(setDate({
        from: propDateRange.from ? propDateRange.from.toISOString() : undefined,
        to: propDateRange.to ? propDateRange.to.toISOString() : undefined
      }));
    }
  }, [propDateRange, dispatch]);

  const fetchMetrics = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!hasGA4Account) {
        setData([]);
        return;
      }

      const response = await axiosInstance.post(
        `/api/analytics/dayAtcReport/${brandId}`,
        {
          startDate: startDate,
          endDate: endDate,
        },
        { withCredentials: true }
      );

      if (response.data && response.data.data) {
        const fetchedData = response.data.data || [];
        setData(fetchedData);

      }
    } catch (error) {
      console.error('Error fetching daywise metrics data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, brandId, hasGA4Account]);

   // Fetch data when dates or brandId change
   useEffect(() => {
    if (startDate && endDate) {
      fetchMetrics();
    }
  }, [startDate, endDate, brandId]); // Only depend on what actually triggers a new fetch

  // Set up polling interval
  useEffect(() => {
    if (!startDate || !endDate) return;
    
    const intervalId = setInterval(() => {
      fetchMetrics();
    }, 15 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [startDate, endDate, brandId]); // Only depend on what affects the interval
  // Listen for refresh trigger from parent
 
  useEffect(() => {
    if (refreshTrigger > 0 && startDate && endDate) {
      fetchMetrics();
    }
  }, [refreshTrigger, startDate, endDate, brandId]); // Only depend on what affects the refresh  // Listen for refresh trigger from parent


  if(isLoading){
    return <Loader isLoading={isLoading} />
  }



  return (
    <ReportTable 
      rows={transformedData} 
      initialPageSize="50"
      visibleColumns={visibleColumns}
      columnOrder={columnOrder}
    />
  );
};

export default DaywiseMetricsPage;